import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase/config'; // Updated import path
import { StockLogo } from '../../utils/stockLogo';

const SlingshotSignals = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: 'volume',
    direction: 'desc'
  });
  const [activeTab, setActiveTab] = useState('long'); // Add this

  const itemsPerPage = 15;
  const title = "Slingshot Pattern Sinyalleri | Smart Portfoy";
  const description = "Teknik analiz slingshot pattern sinyalleri ile trading fırsatlarını takip edin.";

  // Add this helper function at the top of the component
  const formatVolume = (volume) => {
    if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(2)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(2)}K`;
    }
    return volume.toString();
  };

  useEffect(() => {
    const fetchSignals = async () => {
      try {
        // Pattern tipine göre koleksiyon yolunu belirle
        const collectionPath = activeTab === 'long' 
          ? 'patterns/asc_slingshot/signals'
          : 'patterns/desc_slingshot/signals';
        
        const signalsRef = collection(db, collectionPath);
        const q = query(signalsRef);
        const querySnapshot = await getDocs(q);
        
        const signalData = [];
        querySnapshot.forEach((doc) => {
          signalData.push({ id: doc.id, ...doc.data() });
        });
        
        setSignals(signalData);
        setCurrentPage(1); // Tab değişiminde ilk sayfaya dön
      } catch (error) {
        console.error('Error fetching signals:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSignals();
  }, [activeTab]); // activeTab'i dependency olarak ekle

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  };

  const getSortedData = () => {
    if (!sortConfig.key) return signals;

    return [...signals].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'created_at') {
        aValue = aValue?.toDate?.() || new Date(aValue);
        bValue = bValue?.toDate?.() || new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <div className="w-4" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const paginatedData = getSortedData().slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(signals.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-gray-300">Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-red-400">Hata: {error}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
       
          <header className={`bg-gradient-to-r ${
            activeTab === 'long' 
              ? 'from-blue-900 to-cyan-900' 
              : 'from-red-900 to-rose-900'
          } py-2`}>
            <div className="container mx-auto px-4 max-w-4xl">
              <h1 className={`mt-20 text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r ${
                activeTab === 'long'
                  ? 'from-blue-400 to-cyan-400'
                  : 'from-red-400 to-rose-400'
              }`}>
                Sapan Sinyalleri
              </h1>
            </div>
          </header>
        

        {/* Pattern Type Tabs */}
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <div className="flex space-x-1 rounded-xl bg-gray-800 p-1">
            <button
              onClick={() => setActiveTab('long')}
              className={`flex-1 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'long'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Long Sapan
            </button>
            <button
              onClick={() => setActiveTab('short')}
              className={`flex-1 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'short'
                  ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Short Sapan
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('symbol')}>
                    <div className="flex items-center space-x-1">
                      <span>Sembol</span>
                      {getSortIcon('symbol')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('pattern_type')}>
                    <div className="flex items-center space-x-1">
                      <span>Pattern Tipi</span>
                      {getSortIcon('pattern_type')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('entry')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Giriş</span>
                      {getSortIcon('entry')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('target')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Hedef</span>
                      {getSortIcon('target')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('volume')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Hacim</span>
                      {getSortIcon('volume')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => handleSort('created_at')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Oluşturma Tarihi</span>
                      {getSortIcon('created_at')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedData.map((signal) => (
                  <tr key={signal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <StockLogo symbol={signal.symbol} />
                        <a
                          href={`https://www.tradingview.com/chart/?symbol=${signal.symbol}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-sm font-semibold hover:underline ${
                            activeTab === 'long'
                              ? 'text-blue-600 dark:text-blue-400 hover:text-cyan-600'
                              : 'text-red-600 dark:text-red-400 hover:text-rose-600'
                          }`}
                        >
                          {signal.symbol}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                      {signal.pattern_type}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right text-gray-900 dark:text-gray-100">
                      ${signal.entry?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right text-gray-900 dark:text-gray-100">
                      ${signal.target?.toFixed(2)}
                    </td>
                    <td className={`px-6 py-4 text-sm font-medium text-right ${
                      activeTab === 'long'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatVolume(signal.volume)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-400">
                      {signal.created_at?.toDate?.().toLocaleString() || new Date(signal.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Önceki
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Sayfa {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Sonraki
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlingshotSignals;